
import React from "react";
import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SmtpConfig } from "@/types/settings";
import { PatchPolicyFormValues } from "../../schema";
import { mockCustomers } from "@/utils/customerGroups";
import { Textarea } from "@/components/ui/textarea";

interface SmtpSettingsFormProps {
  initialData: SmtpConfig | null;
  onSubmit: (data: SmtpConfig) => void;
  onCancel: () => void;
  form: UseFormReturn<PatchPolicyFormValues>;
}

export function SmtpSettingsForm({ initialData, onSubmit, onCancel, form }: SmtpSettingsFormProps) {
  const [formData, setFormData] = React.useState<SmtpConfig>({
    id: initialData?.id || "",
    name: initialData?.name || "",
    customerId: initialData?.customerId || "global",
    isDefault: initialData?.isDefault || false,
    host: initialData?.host || "",
    port: initialData?.port || 587,
    username: initialData?.username || "",
    password: initialData?.password || "",
    fromAddress: initialData?.fromAddress || "",
    description: initialData?.description || ""
  });

  const handleChange = (field: keyof SmtpConfig, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-4">
      <div className="grid gap-4 grid-cols-2">
        <div className="space-y-2">
          <FormLabel>Configuration Name</FormLabel>
          <Input 
            value={formData.name} 
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="Configuration Name"
            required
          />
        </div>

        <div className="space-y-2">
          <FormLabel>Assign to Customer</FormLabel>
          <Select 
            value={formData.customerId} 
            onValueChange={(value) => handleChange("customerId", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="global">Global (All Customers)</SelectItem>
              {mockCustomers.map((customer) => (
                <SelectItem key={customer.id} value={customer.id}>
                  {customer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <FormLabel>Description</FormLabel>
        <Textarea
          value={formData.description}
          onChange={(e) => handleChange("description", e.target.value)}
          placeholder="Describe this configuration..."
          rows={2}
        />
      </div>

      <div className="grid gap-4 grid-cols-2">
        <div className="space-y-2">
          <FormLabel>SMTP Host</FormLabel>
          <Input 
            value={formData.host} 
            onChange={(e) => handleChange("host", e.target.value)}
            placeholder="smtp.example.com"
            required
          />
        </div>

        <div className="space-y-2">
          <FormLabel>SMTP Port</FormLabel>
          <Input 
            value={formData.port}
            type="number"
            onChange={(e) => handleChange("port", Number(e.target.value))}
            placeholder="587"
            required
          />
        </div>
      </div>

      <div className="grid gap-4 grid-cols-2">
        <div className="space-y-2">
          <FormLabel>SMTP Username</FormLabel>
          <Input 
            value={formData.username}
            onChange={(e) => handleChange("username", e.target.value)}
            placeholder="username"
          />
        </div>

        <div className="space-y-2">
          <FormLabel>SMTP Password</FormLabel>
          <Input 
            value={formData.password}
            type="password"
            onChange={(e) => handleChange("password", e.target.value)}
            placeholder="••••••••"
          />
        </div>
      </div>

      <div className="space-y-2">
        <FormLabel>From Email Address</FormLabel>
        <Input 
          value={formData.fromAddress}
          type="email"
          onChange={(e) => handleChange("fromAddress", e.target.value)}
          placeholder="noreply@example.com"
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {initialData ? "Update" : "Create"} Configuration
        </Button>
      </div>
    </form>
  );
}
